// src/components/CardDisplay.js
import React, { useState } from 'react';
import './CardDisplay.css';

const CardDisplay = ({
                         card,
                         size = 'medium',
                         showStats = true,
                         interactive = true,
                         onClick = null,
                         isSelected = false,
                         isOwned = true
                     }) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Safe number utility
    const safeNumber = (value, fallback = 0) => {
        const num = Number(value);
        return isNaN(num) ? fallback : num;
    };

    const handleCardClick = () => {
        if (interactive && onClick) {
            onClick(card);
        }
        if (interactive) {
            setIsFlipped(!isFlipped);
        }
    };

    const handleImageError = () => {
        setImageError(true);
    };

    const getRarityGlow = (rarity) => {
        return {
            boxShadow: `0 0 20px ${rarity.borderColor}, 0 0 40px ${rarity.borderColor}40`,
            borderColor: rarity.borderColor
        };
    };

    const getStatBar = (value, maxValue = 100) => {
        const safeValue = safeNumber(value, 0);
        const safeMaxValue = safeNumber(maxValue, 100);
        const percentage = Math.min(100, Math.max(0, (safeValue / safeMaxValue) * 100));

        return (
            <div className="stat-bar">
                <div
                    className="stat-fill"
                    style={{
                        width: `${percentage}%`,
                        backgroundColor: card.rarity.color
                    }}
                />
            </div>
        );
    };

    // Determine card type and show appropriate stats
    const getCardStats = () => {
        if (!showStats) return null;

        // Move cards have different stats
        if (card.type === 'Move') {
            return (
                <div className="card-stats">
                    <div className="stat-row">
                        <div className="stat">
                            <span className="stat-label">🔺 Triangles</span>
                            <span className="stat-value">{safeNumber(card.triangles)}</span>
                            {getStatBar(card.triangles, 7)}
                        </div>
                        <div className="stat">
                            <span className="stat-label">📋 Difficulty</span>
                            <span className="stat-value">{card.difficulty || 'Beginner'}</span>
                        </div>
                    </div>
                    <div className="stat-row">
                        <div className="stat">
                            <span className="stat-label">💪 Min Reps</span>
                            <span className="stat-value">{safeNumber(card.minReps, 1)}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">🎯 Max Reps</span>
                            <span className="stat-value">{safeNumber(card.maxReps, card.triangles)}</span>
                        </div>
                    </div>
                </div>
            );
        }

        // Battle cards with 4-corner system
        if (card.type === 'Battle') {
            return (
                <div className="card-stats">
                    <div className="stat-row">
                        <div className="stat">
                            <span className="stat-label">🛡️ Top L</span>
                            <span className="stat-value">{safeNumber(card.topLeft)}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">⚡ Top R</span>
                            <span className="stat-value">{safeNumber(card.topRight)}</span>
                        </div>
                    </div>
                    <div className="stat-row">
                        <div className="stat">
                            <span className="stat-label">⚔️ Bot L</span>
                            <span className="stat-value">{safeNumber(card.bottomLeft)}</span>
                        </div>
                        <div className="stat">
                            <span className="stat-label">💰 Bot R</span>
                            <span className="stat-value">{safeNumber(card.bottomRight)}</span>
                        </div>
                    </div>
                </div>
            );
        }

        // Traditional trainer cards (attack, defense, health, energy)
        return (
            <div className="card-stats">
                <div className="stat-row">
                    <div className="stat">
                        <span className="stat-label">⚔️ Attack</span>
                        <span className="stat-value">{safeNumber(card.attack)}</span>
                        {getStatBar(card.attack)}
                    </div>
                    <div className="stat">
                        <span className="stat-label">🛡️ Defense</span>
                        <span className="stat-value">{safeNumber(card.defense)}</span>
                        {getStatBar(card.defense)}
                    </div>
                </div>
                <div className="stat-row">
                    <div className="stat">
                        <span className="stat-label">❤️ Health</span>
                        <span className="stat-value">{safeNumber(card.health)}</span>
                        {getStatBar(card.health, 150)}
                    </div>
                    <div className="stat">
                        <span className="stat-label">⚡ Energy</span>
                        <span className="stat-value">{safeNumber(card.energy)}</span>
                        {getStatBar(card.energy)}
                    </div>
                </div>
            </div>
        );
    };

    // Calculate power rating based on card type
    const getPowerRating = () => {
        if (card.type === 'Move') {
            // For move cards, use triangles as power indicator
            const triangles = safeNumber(card.triangles, 1);
            return Math.round((triangles / 7) * 100); // Scale to 0-100
        }

        if (card.type === 'Battle') {
            // For battle cards, sum all corners
            const total = safeNumber(card.topLeft) + safeNumber(card.topRight) +
                safeNumber(card.bottomLeft) + safeNumber(card.bottomRight);
            return Math.round(total / 4);
        }

        // For trainer cards, use traditional calculation
        const attack = safeNumber(card.attack);
        const defense = safeNumber(card.defense);
        const health = safeNumber(card.health);
        const energy = safeNumber(card.energy);

        return Math.round((attack + defense + health + energy) / 4);
    };

    if (!card) return null;

    return (
        <div
            className={`battle-card ${size} ${isSelected ? 'selected' : ''} ${!isOwned ? 'locked' : ''} ${interactive ? 'interactive' : ''}`}
            onClick={handleCardClick}
            style={getRarityGlow(card.rarity)}
        >
            <div className={`card-inner ${isFlipped ? 'flipped' : ''}`}>
                {/* Front of Card */}
                <div className="card-front">
                    {/* Card Header */}
                    <div className="card-header">
                        <span className="card-number">#{card.cardNumber || 'N/A'}</span>
                        <span className="card-rarity" style={{ color: card.rarity.color }}>
                            {card.rarity.name}
                        </span>
                    </div>

                    {/* Card Image */}
                    <div className="card-image-container">
                        {!imageError ? (
                            <img
                                src={card.image}
                                alt={card.name}
                                className="card-image"
                                onError={handleImageError}
                            />
                        ) : (
                            <div className="card-image-fallback">
                                <span className="fallback-icon">
                                    <span role="img" aria-label="card">🃏</span>
                                </span>
                            </div>
                        )}

                        {/* Card Type Badge */}
                        <div className={`card-type-badge ${card.type.toLowerCase()}`}>
                            {card.type}
                        </div>

                        {/* Locked Overlay */}
                        {!isOwned && (
                            <div className="locked-overlay">
                                <span className="lock-icon">
                                    <span role="img" aria-label="locked">🔒</span>
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Card Name */}
                    <div className="card-name">
                        <h3>{card.name}</h3>
                        <span className="card-category">{card.category || 'Unknown'}</span>
                    </div>

                    {/* Stats Display */}
                    {getCardStats()}

                    {/* Quick Abilities Preview */}
                    <div className="abilities-preview">
                        {(card.abilities || []).slice(0, 2).map((ability, index) => (
                            <span key={index} className="ability-tag">
                                {ability}
                            </span>
                        ))}
                        {(card.abilities || []).length > 2 && (
                            <span className="ability-more">+{(card.abilities || []).length - 2} more</span>
                        )}
                    </div>
                </div>

                {/* Back of Card */}
                <div className="card-back">
                    <div className="card-back-content">
                        {/* Portrait */}
                        <div className="card-portrait">
                            <img
                                src={card.portrait}
                                alt={`${card.name} portrait`}
                                onError={(e) => e.target.style.display = 'none'}
                            />
                        </div>

                        {/* Full Description */}
                        <div className="card-description">
                            <p>{card.description}</p>
                        </div>

                        {/* All Abilities */}
                        <div className="abilities-full">
                            <h4>Abilities:</h4>
                            {(card.abilities || []).map((ability, index) => (
                                <div key={index} className="ability-item">
                                    <span className="ability-name">{ability}</span>
                                </div>
                            ))}
                        </div>

                        {/* Battle Tips */}
                        <div className="battle-tips">
                            <h4>Battle Strategy:</h4>
                            <p>
                                {card.type === 'Move' ? (
                                    `This ${card.difficulty} exercise targets ${card.category}. ` +
                                    `Perform ${safeNumber(card.triangles)} variations for maximum effectiveness.`
                                ) : card.type === 'Battle' ? (
                                    `Use corners strategically: Attack (${safeNumber(card.bottomLeft)}), ` +
                                    `Defense (${safeNumber(card.topLeft)}), flexibility in round 3.`
                                ) : (
                                    <>
                                        {safeNumber(card.attack) > 85 ? "High attack power - great for offensive strategies. " : ""}
                                        {safeNumber(card.defense) > 85 ? "Strong defense - excellent for defensive play. " : ""}
                                        {safeNumber(card.health) > 100 ? "High health makes this card very durable. " : ""}
                                        {safeNumber(card.energy) > 90 ? "High energy allows for powerful special moves. " : ""}
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Card Power Rating */}
            <div className="card-power-rating">
                <span className="power-label">Power</span>
                <span className="power-value">
                    {getPowerRating()}
                </span>
            </div>
        </div>
    );
};

export default CardDisplay;